import { PageIcon } from "@/components/PageIcon";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PackageX, CheckCircle2 } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";

const TABS = [["open", "Open"], ["resolved", "Resolved"], ["all", "All"]];
const n = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function Row({ r, canResolve, onResolve }) {
  return (
    <tr className="border-b border-slate-100 align-top" data-testid={`short-pick-row-${r.id}`}>
      <td className="py-2.5 px-3">
        {r.job_legacy_id ? <Link to={`/tasks/${r.job_legacy_id}`} className="font-semibold text-indigo-600 hover:underline">{r.job_legacy_id}</Link> : <span className="text-slate-500">{r.source_ref}</span>}
        {r.job_title && <div className="text-[11px] text-slate-500">{r.job_title}</div>}
      </td>
      <td className="py-2.5 px-3">{r.customer || "—"}</td>
      <td className="py-2.5 px-3 whitespace-nowrap">{r.shift_date || "—"}</td>
      <td className="py-2.5 px-3">{r.vehicle || "—"}</td>
      <td className="py-2.5 px-3 font-medium text-slate-800">{r.product}<div className="text-[11px] text-slate-400">{r.sku}</div></td>
      <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-rose-600" data-testid={`short-pick-qty-${r.id}`}>{n(r.qty_short)}<div className="text-[11px] font-normal text-slate-400">{n(r.qty_picked)}/{n(r.qty_required)}</div></td>
      <td className="py-2.5 px-3">{r.reason || "—"}</td>
      <td className="py-2.5 px-3">{r.picker || "—"}</td>
      <td className="py-2.5 px-3 whitespace-nowrap text-xs text-slate-500">{formatDateTime(r.picked_at || r.created_at)}</td>
      <td className="py-2.5 px-3 text-right">
        {r.status === "open" ? (canResolve && <Button size="sm" variant="outline" onClick={() => onResolve(r)} data-testid={`short-pick-resolve-${r.id}`}>Resolve</Button>)
          : <span className="inline-flex items-center gap-1 text-xs text-emerald-600" title={`${r.resolution} · ${r.resolved_by}`}><CheckCircle2 className="w-3.5 h-3.5" />{r.resolution === "repicked_full" ? "Re-picked" : "Resolved"}</span>}
      </td>
    </tr>
  );
}

export default function ShortPicksPage() {
  const { canAny } = useAuth();
  const [tab, setTab] = useState("open");
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/short-picks", { params: { status: tab } }).then((r) => setData(r.data)).catch(() => setData({ items: [], open: 0 })), [tab]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (r) => {
    const note = window.prompt(`Resolve short pick for ${r.product}? Optional note:`, "");
    if (note === null) return;
    try { await api.post(`/short-picks/${r.id}/resolve`, { note }); toast.success("Short pick resolved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resolve"); }
  };

  return (
    <div className="px-4 sm:px-8 pt-3 sm:pt-4 pb-6 sm:pb-8" data-testid="short-picks-page">
      <div className="flex items-center gap-3 mb-4">
        <PageIcon />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Short picks</h1>
          <p className="text-sm text-slate-500">Reported by pickers when a list is marked ready for loading{data ? ` · ${data.open} open` : ""}</p>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`short-picks-tab-${k}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${tab === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}>{label}</button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>{["Job", "Customer", "Shift date", "Van", "Product", "Qty short", "Reason", "Picker", "Time", ""].map((h) => <th key={h} className={`py-2.5 px-3 font-semibold ${h === "Qty short" ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {(data?.items || []).map((r) => <Row key={r.id} r={r} canResolve={canAny(["stock.manage", "operations.manage"])} onResolve={resolve} />)}
          </tbody>
        </table>
        {data && data.items.length === 0 && <p className="p-6 text-center text-sm text-slate-500" data-testid="short-picks-empty">No {tab === "all" ? "" : tab} short picks.</p>}
      </div>
    </div>
  );
}
